import { supabase } from '../lib/supabase'
import { USER_ROLES } from '../config/roles'
import type { Operadora, UsuarioOperadora, UserRole } from '../types'

const db = supabase

export async function getOperadorasDoUsuario(userId: string): Promise<UsuarioOperadora[]> {
  const { data, error } = await db
    .from('usuarios_operadoras')
    .select('*, operadoras(*)')
    .eq('user_id', userId)
    .eq('ativo', true)
    .order('created_at', { ascending: true })

  if (error) throw new Error(`Erro ao buscar operadoras do usuário: ${error.message}`)

  return (data ?? []) as UsuarioOperadora[]
}

export async function criarOperadora(
  userId: string,
  dados: Omit<Operadora, 'id' | 'created_at'> & { cpf?: string | null }
): Promise<Operadora> {
  const { data: op, error: opErr } = await db
    .from('operadoras')
    .insert(dados)
    .select('*')
    .single()

  if (opErr || !op) {
    throw new Error(opErr?.message ?? 'Erro ao criar operadora.')
  }

  const { error: vinErr } = await db
    .from('usuarios_operadoras')
    .upsert({
      user_id: userId,
      operadora_id: op.id,
      papel: 'admin' as UserRole,
      ativo: true,
    }, {
      onConflict: 'user_id,operadora_id',
    })

  if (vinErr) {
    await db.from('operadoras').delete().eq('id', op.id)
    throw new Error(
      'Não foi possível concluir a criação da operadora agora. ' +
      'Nenhum dado parcial foi mantido. Tente novamente em instantes.'
    )
  }

  return op as Operadora
}

export async function atualizarOperadora(
  id: string,
  dados: Partial<Omit<Operadora, 'id' | 'created_at'>>
): Promise<void> {
  const { error } = await db
    .from('operadoras')
    .update(dados)
    .eq('id', id)

  if (error) throw new Error(error.message)
}

export async function solicitarExclusaoOperadora(params: {
  operadoraId: string
  operadoraNome: string
  email: string
  senha: string
}): Promise<void> {
  const { operadoraId, operadoraNome, email, senha } = params

  const { error: authError } = await supabase.auth.signInWithPassword({
    email,
    password: senha,
  })

  if (authError) {
    throw new Error('Senha incorreta. A exclusão não foi solicitada.')
  }

  const { data, error } = await supabase.functions.invoke('confirmar-exclusao-operadora', {
    body: {
      operadora_id: operadoraId,
      operadora_nome: operadoraNome,
    },
  })

  if (error) {
    throw new Error(error.message || 'Não foi possível solicitar a confirmação por e-mail.')
  }

  if (data?.error) {
    throw new Error(data.error)
  }
}

export async function getPapelUsuario(
  userId: string,
  operadoraId: string
): Promise<UserRole | null> {
  const { data, error } = await db
    .from('usuarios_operadoras')
    .select('papel')
    .eq('user_id', userId)
    .eq('operadora_id', operadoraId)
    .maybeSingle()

  if (error) throw new Error(error.message)

  return (data?.papel as UserRole) ?? null
}

export async function validarOperadoraAtiva(
  userId: string,
  operadoraId: string,
): Promise<{ operadora: Operadora; papel: UserRole } | null> {
  const { data, error } = await db
    .from('usuarios_operadoras')
    .select('papel, ativo, operadoras(*)')
    .eq('user_id', userId)
    .eq('operadora_id', operadoraId)
    .eq('ativo', true)
    .maybeSingle()

  if (error) {
    throw new Error('Não foi possível validar a operadora ativa no momento.')
  }

  const papel = data?.papel as UserRole | undefined
  const operadoraRaw = Array.isArray(data?.operadoras)
    ? data?.operadoras[0]
    : data?.operadoras

  if (!operadoraRaw || !papel || !USER_ROLES.includes(papel)) {
    return null
  }

  return {
    operadora: operadoraRaw as Operadora,
    papel,
  }
}
